import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '../UI/Dialogs/ConfirmationDialog';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

interface UnsavedEditsPromptProps {
    isDirty: boolean;
    /** Whether those edits are the ones on screen. False means they belong to another budget version (the
     *  Overview's store is per version), so the dialog has to say so — otherwise it blocks navigation over
     *  a page with nothing marked, no Save button on a read-only version, and no explanation. */
    onCurrentView?: boolean;
}

// The guard that stops a route change from throwing a grid page's pending edits away, with the dialog it
// needs (CALL-779). Its own component because it has to be mounted whatever else the page renders: it
// used to live inside the save bars, and the Overview hides its bar on a read-only version — so
// switching to a snapshot removed the warning along with the Save button, and a page of unsaved figures
// could then be navigated away from without a word.
//
// Not the scenario page's body text: that one names a scenario ("This scenario has unsaved changes"),
// and this sits on pages of master-data grids.
const UnsavedEditsPrompt = ({ isDirty, onCurrentView = true }: UnsavedEditsPromptProps) => {
    const { t } = useTranslation('budget');
    const guard = useUnsavedChangesGuard(isDirty);

    return (
        <ConfirmationDialog
            open={guard.isConfirmOpen}
            title={t('dialog.unsavedChangesTitle')}
            text={onCurrentView ? t('dialog.unsavedEditsBody') : t('dialog.unsavedEditsOtherVersionBody')}
            onClose={guard.cancelLeave}
            onConfirm={guard.confirmLeave}
        />
    );
};

export default UnsavedEditsPrompt;
